import type { Client } from '@/types/client';
import { roundNutritionDisplay } from '@/utils/nutrition-display-format';
import { resolveClientTargetCalories } from '@/utils/client-goals';
import {
  getSuggestedRdaIntakeStatus,
  getSuggestedRdaIntakeStatusFromTarget,
  getSuggestedRdaMinimumTargetIntakeStatus,
} from './suggestedRdaTone';

export interface ClientDetailsV2AnalysisTabProps {
  client: Client;
  clientDisplayName: string;
}

export type Segment = {
  value: number;
  color: string;
};

export type RingArcSegment = {
  path: string;
  color: string;
};

export type MacroShare = {
  carbs: number;
  protein: number;
  fat: number;
};

/** Grams per fat subtype from API (`macros.grams` or flat nutrition); no client-side splits of total fat. */
export type FatSubtypeGrams = {
  monounsaturated: number;
  polyunsaturated: number;
  trans: number;
  saturated: number;
};

/** Grams per kg of body weight from API (`macros.gramsPerKgBodyWeight`) */
export type GramsPerKgBodyWeightApi = {
  carbs: number;
  protein: number;
  fat: number;
  fibre: number;
  sugar: number;
  saturatedFat: number;
};

/** Backend `macros.macroDistributionPercent` — shares of total energy (%), plus `denominatorKcal` for the base intake. */
export type MacroDistributionPercentApi = {
  denominatorKcal: number;
  protein: number;
  carbohydrate: number;
  totalFat: number;
  sugar: number;
  saturatedFat: number;
  mufa: number;
  pufa: number;
  alcohol: number;
  transFat: number;
  /** Present when the API includes fibre as % of total energy. */
  dietaryFibre?: number;
};

export type KpiItem = {
  label: string;
  value: number;
  unit: string;
  target: number;
};

export type MealMacro = {
  name: string;
  kcal: number;
  carbs: number;
  protein: number;
  fat: number;
  fibre: number;
};

export type EnergyByMealItem = {
  name: string;
  percent: number;
  kcal: number;
};

/** Optional `nutrition` grams from meal-plan daywise/cumulative overlay (v2 only). */
export type MealPlanNutritionReportDetail = {
  starch?: number;
  oligosaccharide?: number;
  sugar?: number;
  /** Free / added sugars when the API sends a dedicated field (not inferred from total sugar). */
  free_sugars?: number;
  omega_3?: number;
  omega_6?: number;
  trans_fat?: number;
  mufa?: number;
  pufa?: number;
  cholesterol?: number;
};

export type MealPlanClinicalTargetRange = {
  min?: number;
  max?: number;
  unit: string;
};

export type MealPlanClinicalTargetRanges = Partial<Record<
  | 'energy'
  | 'protein'
  | 'carbs'
  | 'fat'
  | 'fibre'
  | 'saturatedFat'
  | 'freeSugars'
  | 'sodium',
  MealPlanClinicalTargetRange
>>;

export type MealPlanClinicalLimit = {
  kind: 'minimum' | 'maximum' | 'range';
  unit: 'g' | 'mg';
  min?: number;
  max?: number;
  minPercentEnergy?: number;
  maxPercentEnergy?: number;
  minGramsPerKg?: number;
  maxGramsPerKg?: number;
  basisCalories?: number;
  basisBodyWeightKg?: number;
  explanation?: string;
};

export type MealPlanClinicalLimits = Partial<Record<
  | 'protein'
  | 'carbs'
  | 'fat'
  | 'fibre'
  | 'saturatedFat'
  | 'freeSugars'
  | 'sodium',
  MealPlanClinicalLimit
>>;

export type AnalysisData = {
  energy: number;
  energyTarget: number;
  protein: number;
  carbs: number;
  fat: number;
  fibre: number;
  water: number;
  proteinTarget: number;
  carbsTarget: number;
  fatTarget: number;
  fibreTarget: number;
  waterTarget: number;
  bodyWeight: number;
  macroShare: MacroShare;
  /** Set when nutritional analysis returns `macros.macroDistributionPercent` (preferred over recomputed shares). */
  macroDistributionPercent: MacroDistributionPercentApi | null;
  /** Grams per kg body weight from API when provided; otherwise null */
  gramsPerKgBodyWeight: GramsPerKgBodyWeightApi | null;
  /** Fat subtype grams from API when provided; otherwise null (no estimated MUFA/PUFA split). */
  fatSubtypeGrams: FatSubtypeGrams | null;
  freeSugar: number;
  saturatedFat: number;
  sugarKcal: number;
  satFatKcal: number;
  carbsKcal: number;
  proteinKcal: number;
  fatKcal: number;
  fibreKcal: number;
  energyByMeal: EnergyByMealItem[];
  mealMacros: MealMacro[];
  kpis: KpiItem[];
  /**
   * When meal-plan nutritional analysis (v2 `daywise` / `cumulative`) is active, maps optional
   * `nutrition` fields used by the Nutrient Report detail rows. Undefined property = key absent from payload.
   */
  mealPlanNutritionReportDetail?: MealPlanNutritionReportDetail;
  mealPlanClinicalTargetRanges?: MealPlanClinicalTargetRanges;
  /** Structured floors, ceilings, and ranges kept separate from recommended targets. */
  mealPlanClinicalLimits?: MealPlanClinicalLimits;
};

export const analysisTabs = ['All Charts', 'Nutrient Report', 'Macros', 'Micronutrients', 'Diet Log'] as const;

export const colors = {
  border: '#dbe6eb',
  textPrimary: '#0f172a',
  textSecondary: '#667085',
  cardBackground: '#ffffff',
  cyan: '#24A7D8',
  teal: '#2FB9B3',
  /** Within ±10% of target (90–110% intake vs target). */
  green: '#10b981',
  amber: '#F59E0B',
  red: '#F04438',
  blue: '#4A90E2',
} as const;

export const sectionCardSx = {
  borderRadius: '16px',
  border: `1px solid ${colors.border}`,
  bgcolor: colors.cardBackground,
  p: { xs: 1.5, sm: 2, md: 2.25 },
  minWidth: 0,
} as const;

export const ringLegend = [
  { key: 'carbs', label: 'Carbs', color: colors.teal },
  { key: 'protein', label: 'Protein', color: colors.cyan },
  { key: 'fat', label: 'Fat', color: colors.amber },
] as const;

export function safeNumber(value: unknown, fallback: number): number {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
}

export function readPositiveGoalNumber(value: unknown): number | null {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function goalPositiveKcal(value: unknown): number {
  return readPositiveGoalNumber(value) ?? 0;
}

function goalRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

export function readGoalAmount(goal: unknown, preferredKeys: string[] = ['value']): number | null {
  const direct = readPositiveGoalNumber(goal);
  if (direct != null) return direct;

  const record = goalRecord(goal);
  if (!record) return null;

  for (const key of preferredKeys) {
    if (key === 'value') {
      const value = readPositiveGoalNumber(record.value);
      if (value != null) return value;
      continue;
    }

    const nested = record[key];
    const nestedDirect = readPositiveGoalNumber(nested);
    if (nestedDirect != null) return nestedDirect;

    const nestedRecord = goalRecord(nested);
    const nestedValue = readPositiveGoalNumber(nestedRecord?.value);
    if (nestedValue != null) return nestedValue;
  }

  return null;
}

export function readClientGoalAmount(
  clientGoals: unknown,
  key: string,
  preferredKeys: string[] = ['value'],
): number | null {
  const goals = goalRecord(clientGoals);
  if (!goals) return null;
  return readGoalAmount(goals[key], preferredKeys);
}

export function readClientGoalGramAmount(clientGoals: unknown, key: string): number | null {
  return readClientGoalAmount(clientGoals, key, ['grams', 'value']);
}

export function readClientMicronutrientAmount(clientGoals: unknown, key: string): number | null {
  const goals = goalRecord(clientGoals);
  const micros = goalRecord(goals?.micronutrients);
  return readGoalAmount(micros?.[key], ['grams', 'value']);
}

export function readClientGoalWaterLiters(clientGoals: unknown, bodyWeightKg: number): number | null {
  const goals = goalRecord(clientGoals);
  const water = goalRecord(goals?.water);
  const value = readGoalAmount(goals?.water, ['liters', 'litres', 'value']);
  if (value == null) return null;

  const rawUnit = String(
    water?.unit
      ?? goalRecord(water?.liters)?.unit
      ?? goalRecord(water?.litres)?.unit
      ?? '',
  ).trim().toLowerCase();
  const compactUnit = rawUnit.replace(/\s+/g, '');

  if (/m[lL]\s*\/\s*kg/i.test(rawUnit) || compactUnit.includes('ml/kg')) {
    return bodyWeightKg > 0 ? (value * bodyWeightKg) / 1000 : null;
  }
  if (compactUnit === 'ml' || compactUnit.includes('millil')) return value / 1000;
  return value;
}

function nestedGoalEnergyKcal(goal: unknown): number {
  if (goal == null) return 0;
  if (typeof goal === 'number' || typeof goal === 'string') return goalPositiveKcal(goal);
  if (typeof goal !== 'object') return 0;
  const g = goal as { value?: unknown; kcal?: { value?: unknown } | null };
  return goalPositiveKcal(g.value) || goalPositiveKcal(g.kcal?.value);
}

/** kcal/day from structured clientGoals when profile EER fields are unset (`calories` / `dailyEnergyExpenditure`). */
export function extractClientGoalEnergyKcal(clientGoals: unknown): number {
  if (!clientGoals || typeof clientGoals !== 'object') return 0;
  const cg = clientGoals as Record<string, unknown>;
  const direct = goalPositiveKcal(cg.eerGoalCalories) || goalPositiveKcal(cg.targetCalories);
  if (direct > 0) return direct;
  const block = cg.calories ?? cg.dailyEnergyExpenditure;
  return nestedGoalEnergyKcal(block);
}

export function clampPercent(value: number): number {
  return Math.max(0, Math.min(200, value));
}

export function buildConicGradient(segments: Segment[]): string {
  if (!segments.length) return 'conic-gradient(#e5e7eb 0deg 360deg)';

  const total = segments.reduce((sum, segment) => sum + Math.max(0, segment.value), 0);
  if (total <= 0) return 'conic-gradient(#e5e7eb 0deg 360deg)';

  let currentAngle = 0;
  const stops = segments
    .map((segment) => {
      const segmentPercent = (Math.max(0, segment.value) / total) * 100;
      const start = currentAngle;
      const end = currentAngle + segmentPercent * 3.6;
      currentAngle = end;
      return `${segment.color} ${start.toFixed(2)}deg ${end.toFixed(2)}deg`;
    })
    .join(', ');

  return `conic-gradient(${stops})`;
}

function degreesToRadians(degrees: number): number {
  return (degrees * Math.PI) / 180;
}

function polarToCartesian(cx: number, cy: number, radius: number, angleInDegrees: number) {
  const angle = degreesToRadians(angleInDegrees);
  return {
    x: cx + radius * Math.cos(angle),
    y: cy + radius * Math.sin(angle),
  };
}

function describeArcPath(
  cx: number,
  cy: number,
  radius: number,
  startAngle: number,
  endAngle: number
): string {
  const start = polarToCartesian(cx, cy, radius, startAngle);
  const end = polarToCartesian(cx, cy, radius, endAngle);
  const largeArcFlag = endAngle - startAngle <= 180 ? 0 : 1;

  return `M ${start.x.toFixed(3)} ${start.y.toFixed(3)} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${end.x.toFixed(3)} ${end.y.toFixed(3)}`;
}

function getAge(dateOfBirth: string | null | undefined): number | null {
  if (!dateOfBirth) return null;

  const date = new Date(dateOfBirth);
  if (Number.isNaN(date.getTime())) return null;

  const now = new Date();
  let age = now.getFullYear() - date.getFullYear();
  const monthDiff = now.getMonth() - date.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < date.getDate())) {
    age -= 1;
  }

  return age >= 0 ? age : null;
}

type StatusColorOptions = {
  /** Fibre and other higher-is-better minimum targets (meal-plan drawer macro rings). */
  isMinimumTarget?: boolean;
  /** When set, uses the same value/target rules as meal-plan day card macro trends. */
  value?: number;
  target?: number;
};

/** Intake-vs-target color + label — same Suggested RDA rules as the meal-plan analysis panel. */
export function getStatusColor(
  progressPercent: number,
  options?: StatusColorOptions,
): { color: string; label: string } {
  if (options?.value != null && options?.target != null && !options.isMinimumTarget) {
    const { color, label } = getSuggestedRdaIntakeStatusFromTarget(options.value, options.target);
    return { color, label };
  }

  if (options?.isMinimumTarget) {
    const { color, label } = getSuggestedRdaMinimumTargetIntakeStatus(progressPercent);
    return { color, label };
  }

  const { color, label } = getSuggestedRdaIntakeStatus(progressPercent);
  return { color, label };
}

export function formatMetric(value: number, decimals = 0): string {
  void decimals;
  return roundNutritionDisplay(value);
}

export function buildSubtitleParts(client: Client, clientDisplayName: string): string[] {
  const age = getAge(client.dateOfBirth);

  return [
    clientDisplayName,
    age != null ? `${age}yr` : null,
    client.gender ? client.gender.charAt(0).toUpperCase() + client.gender.slice(1) : null,
    Number.isFinite(Number(client.weightKg)) ? `${Math.round(Number(client.weightKg))}kg` : null,
    Number.isFinite(Number(client.heightCm)) ? `${Math.round(Number(client.heightCm))}cm` : null,
  ].filter(Boolean) as string[];
}

/** Single-line subtitle for the analysis header (Figma: middle-dot separators, optional health goal). */
export function buildAnalysisHeaderSubtitleLine(client: Client, clientDisplayName: string): string {
  const parts = buildSubtitleParts(client, clientDisplayName);
  const goal =
    Array.isArray(client.healthGoals)
      ? client.healthGoals.find((g): g is string => typeof g === 'string' && g.trim().length > 0)?.trim()
      : undefined;
  const merged =
    goal && !parts.some((p) => p.includes(goal))
      ? [...parts, goal]
      : parts;
  return merged.join(' · ');
}

/** Nutrient Report tab: avoids repeating name / demographics shown above under Detailed Analysis. */
export function buildNutrientReportTabHeaderSubtitle(client: Client): string {
  const goal =
    Array.isArray(client.healthGoals)
      ? client.healthGoals.find((g): g is string => typeof g === 'string' && g.trim().length > 0)?.trim()
      : undefined;
  return goal ? `${goal} · Compared to reference targets` : 'Compared to dietary reference targets';
}

/** Day dropdown fallback when analysis payload has no day list. */
export const analysisHeaderDayCount = 1;

export function buildAnalysisData(client: Client): AnalysisData {
  const eerKcal = safeNumber(resolveClientTargetCalories(client), 0);
  const energyTarget = eerKcal > 0 ? eerKcal : extractClientGoalEnergyKcal(client.clientGoals);
  const bodyWeight = safeNumber(client.weightKg, 0);
  const proteinTarget = readClientGoalGramAmount(client.clientGoals, 'protein') ?? 0;
  const carbsTarget = readClientGoalGramAmount(client.clientGoals, 'carbohydrates') ?? 0;
  const fatTarget = readClientGoalGramAmount(client.clientGoals, 'totalFat') ?? 0;
  const fibreTarget = readClientGoalAmount(client.clientGoals, 'dietaryFibre', ['grams', 'value']) ?? 0;
  const waterTarget = readClientGoalWaterLiters(client.clientGoals, bodyWeight) ?? 0;

  // Keep baseline neutral; API payload overlays real intake values.
  const energy = 0;
  const protein = 0;
  const carbs = 0;
  const fat = 0;
  const fibre = 0;
  const water = 0;
  const freeSugar = 0;
  const saturatedFat = 0;

  // Baseline only (no meal-plan analysis yet): all kcals stay 0 until merge overlays API `macros.kcal`.
  const carbsKcal = 0;
  const sugarKcal = 0;
  const proteinKcal = 0;
  const fatKcal = 0;
  const satFatKcal = 0;
  const fibreKcal = 0;

  const macroShare: MacroShare = { carbs: 0, protein: 0, fat: 0 };
  const macroDistributionPercent: MacroDistributionPercentApi | null = null;
  const gramsPerKgBodyWeight: GramsPerKgBodyWeightApi | null = null;
  const fatSubtypeGrams: FatSubtypeGrams | null = null;
  const energyByMeal: EnergyByMealItem[] = [];
  const mealMacros: MealMacro[] = [];

  const kpis: KpiItem[] = [
    { label: 'Energy', value: energy, unit: 'kcal', target: energyTarget },
    { label: 'Protein', value: protein, unit: 'g', target: proteinTarget },
    { label: 'Carbs', value: carbs, unit: 'g', target: carbsTarget },
    { label: 'Fat', value: fat, unit: 'g', target: fatTarget },
    { label: 'Fibre', value: fibre, unit: 'g', target: fibreTarget },
    { label: 'Water', value: water, unit: 'L', target: waterTarget },
  ];

  const additionalGoalKpis = [
    {
      label: 'Free Sugars',
      unit: 'g',
      target: readClientGoalGramAmount(client.clientGoals, 'freeSugars') ?? Number.NaN,
    },
    {
      label: 'Saturated Fat',
      unit: 'g',
      target: readClientGoalGramAmount(client.clientGoals, 'saturatedFat') ?? Number.NaN,
    },
    {
      label: 'Trans Fat',
      unit: 'g',
      target: readClientGoalGramAmount(client.clientGoals, 'transFat') ?? Number.NaN,
    },
    {
      label: 'Monounsaturated Fat',
      unit: 'g',
      target:
        readClientGoalGramAmount(client.clientGoals, 'mufa')
        ?? readClientMicronutrientAmount(client.clientGoals, 'mufa')
        ?? Number.NaN,
    },
    {
      label: 'Polyunsaturated Fat',
      unit: 'g',
      target:
        readClientGoalGramAmount(client.clientGoals, 'pufa')
        ?? readClientMicronutrientAmount(client.clientGoals, 'pufa')
        ?? Number.NaN,
    },
    {
      label: 'Omega-3',
      unit: 'g',
      target:
        readClientGoalGramAmount(client.clientGoals, 'omega_3')
        ?? readClientMicronutrientAmount(client.clientGoals, 'omega_3')
        ?? Number.NaN,
    },
    {
      label: 'Alcohol',
      unit: 'g',
      target: readClientGoalGramAmount(client.clientGoals, 'alcohol') ?? Number.NaN,
    },
    {
      label: 'Sodium',
      unit: 'mg',
      target: readClientGoalAmount(client.clientGoals, 'sodium') ?? Number.NaN,
    },
  ];

  const existingKpiLabels = new Set(kpis.map((item) => item.label.trim().toLowerCase()));
  additionalGoalKpis.forEach((item) => {
    const normalizedLabel = item.label.trim().toLowerCase();
    if (existingKpiLabels.has(normalizedLabel) || !Number.isFinite(item.target) || item.target <= 0) return;
    kpis.push({
      label: item.label,
      value: 0,
      unit: item.unit,
      target: item.target,
    });
    existingKpiLabels.add(normalizedLabel);
  });

  return {
    energy,
    energyTarget,
    protein,
    carbs,
    fat,
    fibre,
    water,
    proteinTarget,
    carbsTarget,
    fatTarget,
    fibreTarget,
    mealPlanClinicalLimits: (client.clientGoals as any)?.clinicalRules?.resolvedTargets?.limits,
    waterTarget,
    bodyWeight,
    macroShare,
    macroDistributionPercent,
    gramsPerKgBodyWeight,
    fatSubtypeGrams,
    freeSugar,
    saturatedFat,
    sugarKcal,
    satFatKcal,
    carbsKcal,
    proteinKcal,
    fatKcal,
    fibreKcal,
    energyByMeal,
    mealMacros,
    kpis,
  };
}

export type MacroRingSegmentColors = {
  carbs: string;
  protein: string;
  fat: string;
};

const DEFAULT_MACRO_RING_SEGMENT_COLORS: MacroRingSegmentColors = {
  carbs: colors.teal,
  protein: colors.cyan,
  fat: colors.amber,
};

/** Figma Macronutrient Analysis — carbs / protein / fat for donut and meal bars. */
export const macroAnalysisFigmaSegmentColors: MacroRingSegmentColors = {
  carbs: '#2ec3b6',
  protein: '#20aedc',
  fat: '#f59e0b',
};

export function buildMacroRingSegments(
  macroShare: MacroShare,
  segmentColors: MacroRingSegmentColors = DEFAULT_MACRO_RING_SEGMENT_COLORS,
): RingArcSegment[] {
  const ringCenter = 50;
  const ringRadius = 42;
  const ringGapAngle = 18;

  const rawSegments = [
    { value: Math.max(0, macroShare.carbs), color: segmentColors.carbs },
    { value: Math.max(0, macroShare.protein), color: segmentColors.protein },
    { value: Math.max(0, macroShare.fat), color: segmentColors.fat },
  ];

  const rawTotal = rawSegments.reduce((sum, segment) => sum + segment.value, 0);
  const segments = rawTotal > 0 ? rawSegments : rawSegments.map((segment) => ({ ...segment, value: 1 }));
  const total = segments.reduce((sum, segment) => sum + segment.value, 0);

  const segmentCount = segments.length;
  const availableAngle = 360 - ringGapAngle * segmentCount;
  let currentStartAngle = -90 - availableAngle / 2;

  return segments.map((segment) => {
    const segmentAngle = availableAngle * (segment.value / total);
    const startAngle = currentStartAngle;
    const endAngle = startAngle + segmentAngle;
    currentStartAngle = endAngle + ringGapAngle;

    return {
      path: describeArcPath(ringCenter, ringCenter, ringRadius, startAngle, endAngle),
      color: segment.color,
    };
  });
}
